import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPaymentProfiles, GetPaymentProfilesApiParameter } from "@insite/client-framework/Services/AccountService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import deletePaymentProfile from "@insite/client-framework/Store/Data/PaymentProfiles/Handlers/DeletePaymentProfile";
import updatePaymentProfile from "@insite/client-framework/Store/Data/PaymentProfiles/Handlers/UpdatePaymentProfile";
import { SetPaymentMethod } from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/PlaceOrder";
import updateEditModal from "@insite/client-framework/Store/Pages/SavedPayments/Handlers/UpdateEditModal";
import translate from "@insite/client-framework/Translate";
import { AccountPaymentProfileModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import { PaymentProfilesContext } from "@insite/content-library/Pages/SavedPaymentsPage";
import Accordion from "@insite/mobius/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Trash2 from "@insite/mobius/Icons/Trash2";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Select, { SelectProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import EditIcon from "@MasterBluePrint/Icons/EditIcon";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import sortBy from "lodash/sortBy";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    updateEditModal,
    updatePaymentProfile,
    deletePaymentProfile,
};
const mapStateToProps = (state: ApplicationState) => {
    const settingsCollection = getSettingsCollection(state);
    return {
        websiteSettings: settingsCollection.websiteSettings,
    };
};
interface OwnProps {
    editPaymentProfile: any;
    showEditForm: any;
    updateList: any;
    selectCreditCard: any;
    cardModalOpen: any;
}
type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

const parameter: GetPaymentProfilesApiParameter = { pageSize: 999 };
export interface SavedPaymentsCardListStyles {
    headerContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    sortByGridItem?: GridItemProps;
    sortBySelect?: SelectProps;
    cardHeaderWrapper?: InjectableCss;
    cardTypeImage?: LazyImageProps;
    descriptionText?: TypographyPresentationProps;
    cardInfoContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    nameOnCardLabelText?: TypographyPresentationProps;
    nameOnCardText?: TypographyPresentationProps;
    expirationLabelText?: TypographyPresentationProps;
    expirationText?: TypographyPresentationProps;
    centerColumnGridItem?: GridItemProps;
    billingAddressLabelText?: TypographyPresentationProps;
    billingAddressText?: TypographyPresentationProps;
    rightColumnGridItem?: GridItemProps;
    linksContainer?: GridContainerProps;
    useAsDefaultGridItem?: GridItemProps;
    useAsDefaultButton?: ButtonPresentationProps;
    editLinkGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    deleteLinkGridItem?: GridItemProps;
    deleteLink?: LinkPresentationProps;
    deleteCardModal?: TwoButtonModalStyles;
    cardInfoWrapper?: InjectableCss;
    expirationContainer?: InjectableCss;
    removeCartLineIcon?: IconPresentationProps;
    savedCardListContainer?: InjectableCss;
}

export const cardListStyles: SavedPaymentsCardListStyles = {
    savedCardListContainer: {
        css:css`
            padding: 0 1.5625rem;
        `,
    },
    headerContainer: { gap: 20 },
    cardInfoContainer: {
        css: css`
            padding: 1rem 0;
            border-bottom: 0.0625rem solid ${themeColors.colorBorderBrands};
        `,
    },
    titleGridItem: { width: [12, 12, 5, 5, 5] },
    titleText: {
        variant: "h3",
        as: "h2",
        css: css`
            margin-bottom: 0;
        `,
    },
    sortByGridItem: { width: [12, 12, 7, 7, 7] },
    sortBySelect: {
        labelPosition: "left",
        labelProps: {
            css: css`
                width: auto;
            `,
        },
        cssOverrides: {
            formField: css`
                margin-bottom: 1rem;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            justify-content: flex-start;
                        `,
                        css`
                            justify-content: flex-start;
                        `,
                        css`
                            justify-content: flex-end;
                        `,
                        css`
                            justify-content: flex-end;
                        `,
                        css`
                            justify-content: flex-end;
                        `,
                    ])}
            `,
        },
    },
    cardHeaderWrapper: {
        css: css`
            display: flex;
        `,
    },
    cardTypeImage: {
        imgProps: {
            style: {
                width: "auto",
            },
        },
    },
    descriptionText: {
        css: css`
            margin: 0 0.625rem;
            color: ${themeColors.colorAddress};
            font-size: ${themeFontSizes.size16};
            font-family: 'Lato',sans-serif;
            letter-spacing: 0;
            line-height: 1.5rem;
            font-weight: ${themeFontWeights.fontWeightNormal};
            display: inline-block;
        `,
    },
    leftColumnGridItem: {
        width: [6, 6, 12, 12, 12],
        css: css`
            flex-direction: column;
            padding-top: 0;
            padding-left: 3.250625rem;
        `,
    },
    nameOnCardLabelText: {
        weight: "bold",
        css: css`
            margin-bottom: .250625rem;
            span:first-child {
                min-width: 6.875rem;
                display: inline-block;
            }
        `,
    },
    nameOnCardText: {
        css: css`
            margin: 0 0.625rem;
            color: ${themeColors.colorAddress};
            font-size: ${themeFontSizes.size16};
            font-family: 'Lato',sans-serif;
            letter-spacing: 0;
            line-height: 1.5rem;
            font-weight: ${themeFontWeights.fontWeightNormal};
            display: inline-block;
        `,
    },
    expirationLabelText: {
        weight: "bold",
        css: css`
            margin-bottom: .250625rem;
            margin-top: 8px;
            min-width: 6.875rem;
            display: inline-block;
            span {
                width: 100%;
            }
        `,
    },
    expirationText: {
        css: css`
            color: ${themeColors.colorAddress};
            font-size: ${themeFontSizes.size16};
            font-family: 'Lato',sans-serif;
            letter-spacing: 0;
            line-height: 1.5rem;
            font-weight: ${themeFontWeights.fontWeightNormal};
            display: inline-block;
        `,
    },
    centerColumnGridItem: {
        width: [6, 6, 12, 12, 12],
        css: css`
            flex-direction: column;
            padding-top: 0;
        `,
    },
    billingAddressLabelText: {
        weight: "bold",
        css: css`
            margin-bottom: .250625rem;
            color: ${themeColors.colorHeadText};
            font-family: 'Roboto', sans-serif;
            font-size: ${themeFontSizes.size16};
            font-weight: ${themeFontWeights.fontWeightMedium};
            letter-spacing: 0;
            line-height: 1.4375rem;
        `,
    },
    billingAddressText: {
        css: css`
            width: 100%;
            margin-left: 3.250625rem;
        `,
    },
    rightColumnGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            flex-direction: column;
            position: relative;

            .card-heading {
                color: ${themeColors.colorHeadText};
                font-family: 'Roboto', sans-serif;
                font-size: ${themeFontSizes.size18};
                font-weight: ${themeFontWeights.fontWeightMedium};
                letter-spacing: 0;
                line-height: 1.4375rem;
                margin-bottom: .9375rem;
                margin-top: 0.3125rem;
            }
        `,
    },
    linksContainer: {
        gap: 20,
        css: css`
            width: 100%;
        `,
    },
    useAsDefaultGridItem: {
        width: 12,
        css: css`
            justify-content: left;
            margin-bottom: 0;
            input[type="radio"] {
                margin-left: -0.0625rem;
            }
        `,
    },
    useAsDefaultButton: {
        variant: "secondary",
        css: css`
            color: ${themeColors.colorAddress} !important;
            font-family: 'Lato',sans-serif;
            font-size: ${themeFontSizes.size15};
            letter-spacing: 0.0106rem;
            line-height: 1.125rem;
            height: auto;
            position: relative;

            padding: 0 !important;
            border: 0 !important;
            margin: 0 !important;
            background: none !important;

            > span {
                font-weight: ${themeFontWeights.fontWeightNormal};            
            }

            &:before {
                content: "";
                box-sizing: border-box;
                display: inline-block;
                height: 1.25rem;
                width: 1.25rem;
                border-radius: 50%;
                border: 0.0625rem solid ${themeColors.colorPrimary};
                margin-right: 0.625rem;
                position: relative;
                top: .250625rem;
            }

            &:after {
                content: "";
                display: block;
                height: 0.625rem;
                width: 0.625rem;
                border-radius: 50%;
                background: ${themeColors.colorWhite};
                position: absolute;
                left: 0.3063rem;
                top: calc(50% - 0.1875rem);
            }

            &.active {
                font-weight: ${themeFontWeights.fontWeightForBold};
                color: ${themeColors.colorAddress} !important;

                &:before {
                    border-color: ${themeColors.colorPrimary};
                    background: ${themeColors.colorWhite};
                }

                &:after {
                    background: ${themeColors.colorPrimary};
                }
            }

            &:hover, &:focus, &:active {
                padding: 0 !important;
                border: 0 !important;
                margin: 0 !important;
                background: none !important;
            }
        `,
    },
    editLinkGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            justify-content: right;
            margin-bottom: 1.25rem;
        `,
    },
    editLink: {
        css: css`
            justify-content: right;
            margin-bottom: 1.25rem;
            margin-left: 1.25rem;
            padding-left: .9375rem;
            margin-right: .9375rem;
            border-left: 0.0625rem solid ${themeColors.colorBorderNew}E4E8F0;
            position: absolute;
            top: 1.25rem;
            right: 0;
        `,
    },
    deleteLinkGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            justify-content: right;
            margin-bottom: 1.25rem;
        `,
    },
    deleteLink: {
        css: css`
            justify-content: right;
            margin-bottom: 1.25rem;
            margin-left: 1.25rem;
            padding-left: .9375rem;
            margin-right: 1.875rem;
            position: absolute;
            top: 1.25rem;
            right: 2.5rem;
        `,
    },
    expirationContainer: {
        css: css`
            width: 100%;
        `,
    },
    cardInfoWrapper: {
        css: css`
            position: relative;

            > div > div {
                width: 100% !important;
            }
        `,
    },
    removeCartLineIcon: { src: Trash2 },
};

const styles = cardListStyles;

const getImageName = (cardType: string) => {
    if (cardType.toLowerCase() === "american express") {
        return "amex";
    }
    if (cardType.toLowerCase() === "discover") {
        return "discover";
    }
    if (cardType.toLowerCase() === "mastercard") {
        return "mc";
    }
    if (cardType.toLowerCase() === "visa") {
        return "visa";
    }
    return "";
};

const SavedPaymentsCardListReviewAndPay: React.FC<Props> = ({
    updatePaymentProfile,
    deletePaymentProfile,
    editPaymentProfile,
    showEditForm,
    updateList,
    selectCreditCard,
    cardModalOpen,
}) => {
    const[selectPaymentProfile,setSelectPaymentProfile]=React.useState<AccountPaymentProfileModel | null>();
    const toasterContext = React.useContext(ToasterContext);
    const selectPaymentProfileHandler = (paymentProfile: AccountPaymentProfileModel) => {
        paymentProfiles?.forEach(x=>{
            if(x.id===paymentProfile.id)
            {
                x.isDefault=true;
            }
            else{
                x.isDefault=false;
            }
        })
        selectCreditCard(paymentProfile);
    };

    const editClickHandler = (paymentProfile: AccountPaymentProfileModel) => {
        editPaymentProfile(paymentProfile);
        showEditForm(true);
    };

    const [deleteCardModalIsOpen, setDeleteCardModalIsOpen] = React.useState(false);
    const [paymentProfileToDelete, setPaymentProfileToDelete] = React.useState<AccountPaymentProfileModel | null>(null);
    const deleteCancelHandler = () => {
        setDeleteCardModalIsOpen(false);
    };

    const deleteSubmitHandler = () => {
        if (!paymentProfileToDelete) {
            return;
        }

        deletePaymentProfile({
            paymentProfileId: paymentProfileToDelete.id,
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Card Deleted"), messageType: "success" });
                cardModalOpen(false);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };
    const [paymentProfiles, setPaymentProfiles] = React.useState<AccountPaymentProfileModel[] | null>();
    const deleteClickHandler = (paymentProfile: AccountPaymentProfileModel) => {
        setPaymentProfileToDelete(paymentProfile);
        setDeleteCardModalIsOpen(true);
    };

    React.useEffect(() => {
        getPaymentProfiles(parameter).then(response => {
            setPaymentProfiles(response.accountPaymentProfiles);
        });
    }, [updateList]);

    React.useEffect(()=>{
        paymentProfiles?.forEach(x=>{
            if( x.isDefault===true)
            {
                selectCreditCard(x);
            }
        })
    },[paymentProfiles]);


    return (
        <>  
            <Typography as="div" data-test-selector="cardList" {...styles.savedCardListContainer}>
                {paymentProfiles?.map((savedPayment, index) => (
                    <Typography key={savedPayment.id} as="div" {...styles.cardInfoWrapper}>
                        <GridContainer {...styles.cardInfoContainer} data-test-selector={`cardBody-${savedPayment.id}`}>
                            <GridItem {...styles.rightColumnGridItem}>
                                <h3 className="card-heading">{translate("Card Information")}</h3>
                                <GridContainer {...styles.linksContainer}>
                                    <GridItem {...styles.useAsDefaultGridItem}>
                                        <Button
                                            {...styles.useAsDefaultButton}
                                            onClick={() => selectPaymentProfileHandler(savedPayment)}
                                            data-test-selector="useAsDefaultButton"
                                            className={`${savedPayment.isDefault ? "active" : ""}`}
                                        >
                                            {translate("Select Credit Card")}
                                            
                                        </Button>

                                        <Link
                                            {...styles.deleteLink}
                                            onClick={() => deleteClickHandler(savedPayment)}
                                            data-test-selector="deleteButton"
                                        >
                                            <VisuallyHidden>{translate("Delete Card")}</VisuallyHidden>
                                            <IconMemo {...styles.removeCartLineIcon} />
                                        </Link>

                                        <Link
                                            {...styles.editLink}
                                            onClick={() => editClickHandler(savedPayment)}
                                            data-test-selector="editButton"
                                        >
                                            <Icon src={EditIcon} />
                                            <VisuallyHidden>{translate("Edit")}</VisuallyHidden>
                                        </Link>
                                    </GridItem>

                                    <GridItem {...styles.leftColumnGridItem}>
                                        <Typography {...styles.nameOnCardLabelText} id="cardNumber">
                                            <Typography>{translate("Card Number")}</Typography>

                                            <Typography {...styles.descriptionText} aria-labelledby="cardNumber">
                                                {`${translate(
                                                    "************",
                                                )} ${savedPayment.maskedCardNumber.substring(
                                                    savedPayment.maskedCardNumber.length - 4,
                                                )}`}
                                            </Typography>
                                        </Typography>
                                        
                                        <Typography {...styles.nameOnCardLabelText} id="nameOnCard">
                                            <Typography>{translate("Name on Card")}</Typography>

                                            <Typography {...styles.nameOnCardText} aria-labelledby="nameOnCard">
                                                {savedPayment.cardHolderName}
                                            </Typography>
                                        </Typography>
                                        <Typography as="div" {...styles.expirationContainer}>
                                            <Typography {...styles.expirationLabelText} id="expirationDate">
                                                <Typography>{translate("Exp. Month")}</Typography>

                                                <Typography {...styles.expirationText} aria-labelledby="expirationDate">
                                                {   `${(savedPayment.expirationDate).substring(0, (savedPayment.expirationDate).lastIndexOf("/"))}`}
                                                </Typography>
                                            </Typography>
        

                                            <Typography {...styles.expirationLabelText} id="expirationYear">
                                                <Typography>{translate("Exp. Year")}</Typography>

                                                <Typography {...styles.expirationText} aria-labelledby="expirationYear">
                                                    {`${(savedPayment.expirationDate).substring((savedPayment.expirationDate).lastIndexOf("/") + 1, (savedPayment.expirationDate).length)}`}
                                                </Typography>
                                            </Typography>
                                        </Typography>
                                    </GridItem>
                                    <GridItem {...styles.centerColumnGridItem}>
                                        <Typography {...styles.billingAddressLabelText} id="billingAddress">
                                            {translate("Billing Address")}
                                        </Typography>
                                        <Typography {...styles.billingAddressText} aria-labelledby="billingAddress">
                                            {[
                                                savedPayment.address1,
                                                savedPayment.address2,
                                                savedPayment.address3,
                                                savedPayment.address4,
                                            ]
                                                .filter(o => !!o)
                                                .join(", ")}
                                            <Typography>{", "}{savedPayment.city}, {savedPayment.state}, {savedPayment.postalCode}, {savedPayment.country}</Typography>
                                        </Typography>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            
                            
                        </GridContainer>
                    </Typography>
                ))}

               
            </Typography>
            {/* <StyledWrapper>
                <Button
                    data-test-selector="addressModal_continue"
                    onClick={() => applyPaymentProfile()}
                >
                    {translate("Continue")}
                </Button>
            </StyledWrapper> */}
            <TwoButtonModal
                {...styles.deleteCardModal}
                modalIsOpen={deleteCardModalIsOpen}
                headlineText={translate("Delete Card")}
                messageText={translate("Are you sure you want to delete this card?")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={deleteCancelHandler}
                onSubmit={deleteSubmitHandler}
                submitTestSelector="submitDeleteCardButton"
            />
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SavedPaymentsCardListReviewAndPay);